import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../primitives/dialog';
import { ActionButton } from '../concept/action-button';
import { Eyebrow } from '../concept/eyebrow';
import { useToast } from '../primitives/use-toast';
import type { ShareLinkData } from '../../lib/interfaces';

/**
 * Share a client's dashboard with them.
 *
 * Replaces a header button that called onShare(true) and said nothing: the
 * link was created, state updated, and the reader saw no change. Sharing is
 * a thing you do once and then need the result of, so the result is the
 * screen.
 *
 * What the client sees is the same dashboard minus coach-only material. Notes
 * are omitted server-side for a share reader, which is worth saying here
 * rather than leaving a coach to wonder.
 */
export const ShareDialog: React.FC<{
  clientID: string;
  clientName: string;
  shared?: ShareLinkData;
  onShare: (enabled: boolean) => Promise<ShareLinkData | undefined>;
  /** What the person is called here: "client", or "customer" on retail. */
  noun?: string;
  /** What their dashboard shows them; retail has no Goal column. */
  summary?: string;
}> = ({
  clientID,
  clientName,
  shared,
  onShare,
  noun = 'client',
  summary = 'Then, Now and Goal',
}) => {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const active = Boolean(shared?.active);
  const link = shared?.code
    ? `${window.location.origin}/dashboard/${clientID}?code=${shared.code}`
    : '';

  const set = async (enabled: boolean) => {
    setBusy(true);
    try {
      await onShare(enabled);
      toast({
        title: enabled ? 'Sharing on' : 'Sharing off',
        description: enabled
          ? `${clientName} can open their dashboard with this link.`
          : 'The previous link no longer works.',
      });
    } catch (e) {
      toast({
        title: 'Could not change sharing',
        description: e instanceof Error ? e.message : 'Try again.',
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard is refused without a user gesture in some browsers, and on
      // http origins. The link is on screen and selectable either way.
      toast({ title: 'Copy it from the field below', variant: 'destructive' });
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          data-testid="share-open"
          className="rounded-lg border border-border bg-card px-3.5 py-2.5 text-xs font-medium leading-none transition-colors hover:bg-shell"
        >
          {active ? 'Sharing on' : `Share with ${noun}`}
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold tracking-tight">
            Share with {clientName}
          </DialogTitle>
          <DialogDescription className="text-[12.5px] leading-relaxed text-foreground-soft">
            They see their own dashboard — {summary} — without your
            notes, which the server omits for a share reader.
          </DialogDescription>
        </DialogHeader>

        {active && link ? (
          <div className="flex flex-col gap-2">
            <Eyebrow>Link</Eyebrow>
            <div className="flex gap-2">
              <input
                readOnly
                value={link}
                data-testid="share-link"
                onFocus={(e) => e.currentTarget.select()}
                className="min-w-0 flex-1 rounded-lg border border-border bg-shell px-3 py-2 font-mono text-[11px] outline-none"
              />
              <ActionButton onClick={copy} testId="share-copy">
                {copied ? 'Copied' : 'Copy'}
              </ActionButton>
            </div>
            <ActionButton
              className="mt-2 self-start"
              disabled={busy}
              onClick={() => set(false)}
              testId="share-off"
            >
              {busy ? 'Working…' : 'Stop sharing'}
            </ActionButton>
          </div>
        ) : (
          <ActionButton
            variant="primary"
            disabled={busy}
            onClick={() => set(true)}
            testId="share-on"
          >
            {busy ? 'Creating…' : 'Create a share link'}
          </ActionButton>
        )}
      </DialogContent>
    </Dialog>
  );
};
